'use client';

import { useEffect, useState } from 'react';

type ClientChromeModule = typeof import('@/components/ClientChrome');

export function DeferredClientChrome({ yandexMetrikaEnabled }: { yandexMetrikaEnabled: boolean }) {
  const [ClientChrome, setClientChrome] = useState<ClientChromeModule['ClientChrome'] | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timeoutId: number | null = null;
    let loadListenerRegistered = false;

    const loadChrome = () => {
      if (cancelled) {
        return;
      }

      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }

      void import('@/components/ClientChrome').then((mod) => {
        if (!cancelled) {
          setClientChrome(() => mod.ClientChrome);
        }
      });
    };

    const scheduleChromeLoad = () => {
      if (cancelled || timeoutId !== null) {
        return;
      }

      timeoutId = window.setTimeout(loadChrome, 3000);
    };

    const handleEarlyInteraction = () => {
      loadChrome();
    };

    const registerEarlyInteractionListeners = () => {
      window.addEventListener('pointerdown', handleEarlyInteraction, { passive: true, once: true });
      window.addEventListener('keydown', handleEarlyInteraction, { passive: true, once: true });
      window.addEventListener('touchstart', handleEarlyInteraction, { passive: true, once: true });
    };

    registerEarlyInteractionListeners();

    if (document.readyState === 'complete') {
      scheduleChromeLoad();
    } else {
      const handleLoad = () => {
        loadListenerRegistered = false;
        scheduleChromeLoad();
      };

      window.addEventListener('load', handleLoad, { once: true });
      loadListenerRegistered = true;

      return () => {
        cancelled = true;
        if (timeoutId !== null) {
          window.clearTimeout(timeoutId);
        }
        if (loadListenerRegistered) {
          window.removeEventListener('load', handleLoad);
        }
        window.removeEventListener('pointerdown', handleEarlyInteraction);
        window.removeEventListener('keydown', handleEarlyInteraction);
        window.removeEventListener('touchstart', handleEarlyInteraction);
      };
    }

    return () => {
      cancelled = true;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      window.removeEventListener('pointerdown', handleEarlyInteraction);
      window.removeEventListener('keydown', handleEarlyInteraction);
      window.removeEventListener('touchstart', handleEarlyInteraction);
    };
  }, []);

  return ClientChrome ? <ClientChrome yandexMetrikaEnabled={yandexMetrikaEnabled} /> : null;
}
